import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { CalendarBlank, ArrowRight, CheckCircle, Flame } from "@phosphor-icons/react";

/**
 * SundayCheckinBanner — visible EVERY day of the week (not just Sunday).
 *
 *   Sunday · not-yet-submitted →  BIG red "SUNDAY CHECK-IN DAY" banner + CTA
 *   Sunday · submitted           →  Green ✓ pill "Coach is reviewing"
 *   Mon–Sat · submitted this wk  →  Green ✓ pill "This week's check-in is in"
 *   Mon–Sat · not submitted      →  Sleek countdown card "X days to Sunday Check-In"
 *
 * Reinforces the weekly ritual on the client Today page — so even on a
 * Wednesday the client sees "SUNDAY IS CHECK-IN DAY" and mentally tracks
 * toward the ritual instead of being surprised by it.
 */
export default function SundayCheckinBanner() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/client/sunday-checkin/current");
        setState(data);
      } catch { /* silent — banner just won't show */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || !state) return null;

  const now = new Date();
  const dow = now.getDay();                    // 0 = Sun, 1 = Mon, ... 6 = Sat
  const isSunday = dow === 0;
  const daysUntilSunday = dow === 0 ? 0 : (7 - dow);
  const submitted = !!state.checkin;

  // ------ Submitted state (shown Sun–Sat until the next iso-week rolls) ------
  if (submitted) {
    return (
      <div className="mb-4 border border-[#00CC66]/30 bg-[#00CC66]/5 px-4 py-2.5 flex items-center gap-3" data-testid="sunday-banner-done">
        <CheckCircle size={16} weight="fill" className="text-[#00CC66] shrink-0" />
        <div className="text-xs font-manrope text-zinc-300 flex-1">
          <span className="text-[#00CC66] font-bold">This week&apos;s Sunday check-in is in.</span> Coach is reviewing.
        </div>
      </div>
    );
  }

  // ------ Sunday · not submitted → hero-style urgent banner ------
  if (isSunday) {
    return (
      <div
        className="mb-4 border-2 border-[#FF3333] bg-gradient-to-br from-[#FF3333]/20 via-[#FF3333]/5 to-transparent p-5 sm:p-6"
        data-testid="sunday-banner-pending"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-[#FF3333] flex items-center justify-center shrink-0">
            <Flame size={24} weight="fill" className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="id-label text-[#FF3333] mb-1">— IT&apos;S ON —</div>
            <div className="font-oswald text-3xl sm:text-4xl uppercase tracking-tight leading-[0.95] text-white mb-2">
              Sunday Check-In Day
            </div>
            <p className="text-zinc-300 font-manrope text-sm sm:text-base leading-relaxed">
              2 minutes. 7 quick questions. Coach uses this to tune your workouts + meals for next week.
              Don&apos;t skip it — this is the difference between guessing and progressing.
            </p>
            <Link
              to="/app/sunday-checkin"
              className="mt-4 inline-flex items-center gap-2 text-sm font-oswald uppercase tracking-[0.15em] font-bold text-white bg-[#FF3333] hover:bg-[#E62E2E] px-4 py-3 transition-colors"
              data-testid="sunday-banner-cta"
            >
              Start My Check-In <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ------ Mon–Sat · not submitted → countdown card so the ritual stays top-of-mind ------
  const dayLabel = daysUntilSunday === 1 ? "TOMORROW" : `${daysUntilSunday} DAYS TO GO`;
  return (
    <div
      className="mb-4 border border-[#FF3333]/30 bg-[#141414] px-4 py-3 flex items-center gap-3"
      data-testid="sunday-banner-countdown"
    >
      <div className="w-9 h-9 border border-[#FF3333]/40 flex items-center justify-center shrink-0">
        <CalendarBlank size={16} weight="fill" className="text-[#FF3333]" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="id-label text-[#FF3333] mb-0.5">— Sunday is check-in day —</div>
        <div className="text-xs sm:text-sm font-manrope text-zinc-300">
          <span className="font-oswald uppercase tracking-wide text-white text-base">{dayLabel}</span>
          {" "}· lock in your week with coach.
        </div>
      </div>
    </div>
  );
}
